import type { LocalizedDict } from '../index'

// Admin full-page Settings shell (features/admin/AdminSettingsShell.tsx).
// Labels only — section ids / routes stay untouched in the component.

export type AdminSettingsShellStrings = {
  groups: {
    company: string
    operations: string
    system: string
    help: string
  }
  items: {
    organization: string
    users: string
    brand: string
    shipping: string
    integrations: string
    partnerAccess: string
    referralProgram: string
    policies: string
    reviewTracking: string
    notifications: string
    security: string
    featureSwitches: string
    appearance: string
    driveMaintenance: string
    help: string
  }
  tabs: {
    loginExperience: string
    catalogBrand: string
    shippingAccounts: string
    shippingInternational: string
    shippingLocations: string
    shippingTags: string
    shippingPrinting: string
    marketplaceApps: string
    paymentGateways: string
    mediaGeneration: string
    researchTools: string
    applePush: string
    etsyListing: string
    partnerApi: string
    partnerCatalog: string
    rewardsSettings: string
    rewardsReferrals: string
    rewardsLedger: string
    messaging: string
    problemReporting: string
    inventoryRollout: string
    viewAs: string
    demoGuide: string
    resources: string
  }
  backToDashboard: string
  settings: string
  searchPlaceholder: string
  navLabel: string
  noMatches: (query: string) => string
  sectionPickerLabel: string
}

export const adminSettingsShellI18n: LocalizedDict<AdminSettingsShellStrings> = {
  en: {
    groups: {
      company: 'Company',
      operations: 'Operations',
      system: 'System',
      help: 'Help',
    },
    items: {
      organization: 'Organization',
      users: 'Users',
      brand: 'Brand',
      shipping: 'Shipping',
      integrations: 'Integrations',
      partnerAccess: 'Partner access',
      referralProgram: 'Referral Program',
      policies: 'Policies',
      reviewTracking: 'Review Tracking',
      notifications: 'Notifications',
      security: 'Security',
      featureSwitches: 'Feature switches',
      appearance: 'Appearance',
      driveMaintenance: 'Drive Maintenance',
      help: 'Help',
    },
    tabs: {
      loginExperience: 'Login screen',
      catalogBrand: 'Catalog PDF brand',
      shippingAccounts: 'Providers',
      shippingInternational: 'International',
      shippingLocations: 'Ship-from locations',
      shippingTags: 'Order tags',
      shippingPrinting: 'Printing',
      marketplaceApps: 'Marketplace apps',
      paymentGateways: 'Payments (Stripe)',
      mediaGeneration: 'Media generation',
      researchTools: 'Research tools',
      applePush: 'Apple push notifications',
      etsyListing: 'Etsy listing push',
      partnerApi: 'API keys',
      partnerCatalog: 'Catalogue sharing',
      rewardsSettings: 'Settings',
      rewardsReferrals: 'Referrals',
      rewardsLedger: 'Ledger',
      messaging: 'Messaging',
      problemReporting: 'Problem reporting',
      inventoryRollout: 'Inventory',
      viewAs: 'Customer panel view',
      demoGuide: 'Demo guide',
      resources: 'Resources',
    },
    backToDashboard: 'Back to Dashboard',
    settings: 'Settings',
    searchPlaceholder: 'Search settings…',
    navLabel: 'Settings navigation',
    noMatches: (query) => `No settings match "${query}".`,
    sectionPickerLabel: 'Settings section',
  },
  tr: {
    groups: {
      company: 'Şirket',
      operations: 'Operasyon',
      system: 'Sistem',
      help: 'Yardım',
    },
    items: {
      organization: 'Organizasyon',
      users: 'Kullanıcılar',
      brand: 'Marka',
      shipping: 'Kargo',
      integrations: 'Entegrasyonlar',
      partnerAccess: 'Partner erişimi',
      referralProgram: 'Referans Programı',
      policies: 'Sözleşmeler',
      reviewTracking: 'Yorum Takibi',
      notifications: 'Bildirimler',
      security: 'Güvenlik',
      featureSwitches: 'Özellik anahtarları',
      appearance: 'Görünüm',
      driveMaintenance: 'Drive Bakımı',
      help: 'Yardım',
    },
    tabs: {
      loginExperience: 'Giriş ekranı',
      catalogBrand: 'Katalog PDF markası',
      shippingAccounts: 'Sağlayıcılar',
      shippingInternational: 'Uluslararası',
      shippingLocations: 'Gönderim adresleri',
      shippingTags: 'Sipariş etiketleri',
      shippingPrinting: 'Yazdırma',
      marketplaceApps: 'Pazar yeri uygulamaları',
      paymentGateways: 'Ödemeler (Stripe)',
      mediaGeneration: 'Medya üretimi',
      researchTools: 'Araştırma araçları',
      applePush: 'Apple anlık bildirimleri',
      etsyListing: 'Etsy ilan gönderimi',
      partnerApi: 'API anahtarları',
      partnerCatalog: 'Katalog paylaşımı',
      rewardsSettings: 'Ayarlar',
      rewardsReferrals: 'Referanslar',
      rewardsLedger: 'Hareketler',
      messaging: 'Mesajlaşma',
      problemReporting: 'Sorun bildirme',
      inventoryRollout: 'Envanter',
      viewAs: 'Müşteri paneli görünümü',
      demoGuide: 'Demo rehberi',
      resources: 'Kaynaklar',
    },
    backToDashboard: 'Panoya dön',
    settings: 'Ayarlar',
    searchPlaceholder: 'Ayarlarda ara…',
    navLabel: 'Ayarlar menüsü',
    noMatches: (query) => `"${query}" ile eşleşen ayar yok.`,
    sectionPickerLabel: 'Ayarlar bölümü',
  },
}
